import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Bus, MapPin, Calendar as CalendarIcon, Users, Shield, Wifi, 
  Clock, Star, ChevronRight, Phone, ArrowRight, Play, ChevronLeft, UserRound
} from 'lucide-react';
import { format, isToday, isTomorrow, addDays, startOfDay } from 'date-fns';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Panashe Coaches actual images
const PANASHE_IMAGES = [
  'https://customer-assets.emergentagent.com/job_panashecoaches/artifacts/bd4gmemv_WhatsApp%20Image%202025-01-01%20at%201.46.28%20PM.jpeg',
  'https://customer-assets.emergentagent.com/job_panashecoaches/artifacts/8a5si0te_FB_IMG_1764014317493.jpg',
  'https://customer-assets.emergentagent.com/job_panashecoaches/artifacts/o6jxard1_FB_IMG_1764014911178.jpg',
];

const PANASHE_VIDEO = 'https://customer-assets.emergentagent.com/job_panashecoaches/artifacts/pxjf0qyh_20260106_114304.mp4';

const ROUTE_BOARDING_LOCATIONS = [
  { id: 'Harare Roadport', name: 'Harare Roadport Terminal, Zimbabwe' },
  { id: 'Highglen', name: 'Highglen, Harare, Zimbabwe' },
  { id: 'Chitungwiza', name: 'Chitungwiza, Zimbabwe' },
  { id: 'Bulawayo', name: 'Bulawayo Terminal, Zimbabwe' },
  { id: 'Beitbridge', name: 'Beitbridge Border (Engen Garage)' },
  { id: 'Durban', name: 'Durban Terminal, South Africa' },
  { id: 'Pietermaritzburg', name: 'Pietermaritzburg Loading Point, South Africa' },
  { id: 'Mooi River', name: 'Mooi River Loading Point, South Africa' },
  { id: 'Johannesburg', name: 'Johannesburg Park Station, South Africa' },
  { id: 'Pretoria', name: 'Pretoria Station, South Africa' },
  // Masvingo Route Towns
  { id: 'Masvingo', name: 'Masvingo Pick/Drop Point, Zimbabwe' },
  { id: 'Ngundu', name: 'Ngundu Halt / Pick Point, Zimbabwe' },
  { id: 'Chibi turnoff', name: 'Chibi Turnoff, Zimbabwe' },
  { id: 'Rutenga', name: 'Rutenga Pick Point, Zimbabwe' },
  { id: 'Lundi', name: 'Lundi Pick Point, Zimbabwe' },
  { id: 'Bubi', name: 'Bubi Pick Point, Zimbabwe' },
  { id: 'Chaka', name: 'Chaka Business Centre, Zimbabwe' },
  { id: 'Mvuma', name: 'Mvuma Pick Point, Zimbabwe' },
  { id: 'Chivhu', name: 'Chivhu Pick Point, Zimbabwe' },
  { id: 'Furtherstone', name: 'Furtherstone Pick Point, Zimbabwe' },
  { id: 'Beatrice', name: 'Beatrice Pick Point, Zimbabwe' },
  // Bulawayo Route Towns
  { id: 'Gweru', name: 'Gweru Pick/Drop Point, Zimbabwe' },
  { id: 'Kwekwe', name: 'Kwekwe Pick/Drop Point, Zimbabwe' },
  { id: 'Kadoma', name: 'Kadoma Pick/Drop Point, Zimbabwe' },
  { id: 'Chegutu', name: 'Chegutu Pick/Drop Point, Zimbabwe' },
  { id: 'Norton', name: 'Norton Pick Point, Zimbabwe' },
  { id: 'Whitehouse', name: 'Whitehouse Pick Point, Zimbabwe' },
  { id: 'Esigodini', name: 'Esigodini Pick Point, Zimbabwe' },
  { id: 'Gwanda', name: 'Gwanda Pick Point, Zimbabwe' },
  { id: 'Colleen Bawn', name: 'Colleen Bawn Pick Point, Zimbabwe' },
  { id: 'West Nicholson', name: 'West Nicholson Pick Point, Zimbabwe' },
  { id: 'Makado', name: 'Makado Pick Point, Zimbabwe' }
];

const HomePage = () => {
  const navigate = useNavigate();
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [date, setDate] = useState(new Date());
  const [passengers, setPassengers] = useState('1');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [stations, setStations] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    // Fetch stations from backend
    axios.get(`${API_URL}/stations`)
      .then(res => setStations(res.data))
      .catch(err => console.error('Failed to load stations'));
    
    // Auto-rotate gallery
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % PANASHE_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: Wifi, title: 'Free WiFi', desc: 'Stay connected throughout your journey' },
    { icon: Shield, title: 'Safe Travel', desc: 'GPS tracked buses with experienced drivers' },
    { icon: Clock, title: 'On-Time', desc: '95% on-time departure rate' },
    { icon: Star, title: 'Comfort', desc: 'Air-conditioned luxury coaches' },
  ];

  const handleSearch = (e) => {
    e.preventDefault();
    if (!origin || !destination || !date) return;
    
    const searchParams = new URLSearchParams({
      origin,
      destination,
      date: date.toISOString().split('T')[0],
      passengers
    });
    navigate(`/search?${searchParams.toString()}`);
  };

  return (
    <PublicLayout>
      {/* Hero Section with Image/Video Gallery */}
      <section className="relative min-h-[700px] flex items-center">
        {/* Background Gallery */}
        <div className="absolute inset-0">
          {!showVideo ? (
            <>
              {PANASHE_IMAGES.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Panashe Coaches bus slideshow ${idx + 1}`}
                  className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                    idx === currentSlide ? 'opacity-100' : 'opacity-0'
                  }`}
                  decoding="async"
                />
              ))}
            </>
          ) : (
            <video
              autoPlay
              muted
              loop
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={PANASHE_VIDEO} type="video/mp4" />
            </video>
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/15 to-transparent" />
        </div>

        {/* Gallery Controls */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 z-20">
          <button 
            onClick={() => setCurrentSlide(prev => (prev - 1 + PANASHE_IMAGES.length) % PANASHE_IMAGES.length)}
            className="p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex gap-2">
            {PANASHE_IMAGES.map((_, idx) => (
              <button
                key={idx}
                onClick={() => { setCurrentSlide(idx); setShowVideo(false); }}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === currentSlide && !showVideo ? 'bg-panashe-gold w-8' : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
            <button
              onClick={() => setShowVideo(true)}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                showVideo ? 'bg-panashe-gold text-black' : 'bg-white/50 text-white hover:bg-white/80'
              }`}
            >
              <Play className="h-3 w-3" />
              Video
            </button>
          </div>
          <button 
            onClick={() => setCurrentSlide(prev => (prev + 1) % PANASHE_IMAGES.length)}
            className="p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-colors"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-16 w-full z-10">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Bus className="h-10 w-10 text-panashe-gold" />
              <span className="text-panashe-gold font-heading text-2xl font-bold uppercase tracking-wider">
                Panashe Coaches
              </span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-4 animate-slide-up">
              Cross-Border
              <span className="block text-panashe-gold">Bus Travel</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Premium coach services connecting Zimbabwe and South Africa. 
              Book your journey today with Panashe Coaches.
            </p>

            {/* Contact Numbers */}
            <div className="flex flex-wrap gap-4 mb-6 animate-slide-up" style={{ animationDelay: '0.15s' }}>
              <a href="tel:+263786563430" className="flex items-center gap-2 text-white hover:text-panashe-gold transition-colors">
                <Phone className="h-5 w-5" />
                <span className="font-medium">+263 78 656 3430</span>
              </a>
              <a href="tel:+263777275154" className="flex items-center gap-2 text-white hover:text-panashe-gold transition-colors">
                <Phone className="h-5 w-5" />
                <span className="font-medium">+263 77 727 5154</span>
              </a>
            </div>

            {/* Search Card */}
            <Card className="bg-white/95 backdrop-blur-sm shadow-2xl animate-slide-up" style={{ animationDelay: '0.2s' }} data-testid="search-card">
              <CardContent className="p-6">
                <form onSubmit={handleSearch} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Origin */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">From (Boarding Location)</label>
                      <Select value={origin} onValueChange={setOrigin}>
                        <SelectTrigger className="w-full h-12 bg-white" data-testid="origin-select">
                          <MapPin className="h-4 w-4 mr-2 text-panashe-red" />
                          <SelectValue placeholder="Select boarding location" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {ROUTE_BOARDING_LOCATIONS.map(loc => (
                            <SelectItem key={loc.id} value={loc.id} data-testid={`origin-${loc.id}`}>
                              {loc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Destination */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">To (Drop-off Location)</label>
                      <Select value={destination} onValueChange={setDestination}>
                        <SelectTrigger className="w-full h-12 bg-white" data-testid="destination-select">
                          <MapPin className="h-4 w-4 mr-2 text-panashe-red" />
                          <SelectValue placeholder="Select drop-off location" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {ROUTE_BOARDING_LOCATIONS.filter(s => s.id !== origin).map(loc => (
                            <SelectItem key={loc.id} value={loc.id} data-testid={`dest-${loc.id}`}>
                              {loc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Date */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-slate-700">Travel Date</label>
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setDate(new Date())}
                            className={`text-xs px-2.5 py-0.5 rounded-md font-semibold transition-all border ${
                              date && isToday(date)
                                ? 'bg-panashe-blue text-white border-panashe-blue shadow-sm'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-300'
                            }`}
                          >
                            Today
                          </button>
                          <button
                            type="button"
                            onClick={() => setDate(addDays(new Date(), 1))}
                            className={`text-xs px-2.5 py-0.5 rounded-md font-semibold transition-all border ${
                              date && isTomorrow(date)
                                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border-slate-300'
                            }`}
                          >
                            Tomorrow
                          </button>
                        </div>
                      </div>
                      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                        <PopoverTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="w-full h-12 justify-start text-left font-normal bg-white"
                            data-testid="date-picker"
                          >
                            <CalendarIcon className="h-4 w-4 mr-2 text-panashe-red" />
                            {date ? (
                              <span>
                                <strong>{format(date, 'EEE, dd MMM yyyy')}</strong>
                                {isToday(date) && <span className="ml-2 text-xs bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">Today</span>}
                                {isTomorrow(date) && <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-bold">Tomorrow</span>}
                              </span>
                            ) : (
                              'Select date'
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(d) => {
                              if (d) {
                                setDate(d);
                                setCalendarOpen(false);
                              }
                            }}
                            disabled={(d) => {
                              const startToday = startOfDay(new Date());
                              return d < startToday;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Passengers */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Passengers</label>
                      <Select value={passengers} onValueChange={setPassengers}>
                        <SelectTrigger className="w-full h-12" data-testid="passengers-select">
                          <Users className="h-4 w-4 mr-2 text-panashe-red" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6].map(n => (
                            <SelectItem key={n} value={String(n)}>
                              {n} Passenger{n > 1 ? 's' : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-panashe-red hover:bg-panashe-red-dark text-white text-lg font-bold"
                    disabled={!origin || !destination}
                    data-testid="search-btn"
                  >
                    Search Buses
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-slate-900 uppercase mb-4">
            Why Choose <span className="text-panashe-red">Panashe</span>?
          </h2>
          <p className="text-center text-slate-600 mb-12 max-w-2xl mx-auto">
            Experience premium cross-border travel with our modern fleet and professional service.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <Card key={idx} className="p-6 text-center hover:-translate-y-1 transition-transform border-2 hover:border-panashe-red/20" data-testid={`feature-${idx}`}>
                <div className="w-14 h-14 bg-panashe-red/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-7 w-7 text-panashe-red" />
                </div>
                <h3 className="font-heading font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet Gallery */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-slate-900 uppercase mb-4">
            Our <span className="text-panashe-red">Fleet</span>
          </h2>
          <p className="text-center text-slate-600 mb-12">
            Modern, comfortable and well-maintained coaches
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PANASHE_IMAGES.map((img, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-lg shadow-lg group cursor-pointer" onClick={() => { setCurrentSlide(idx); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <img src={img} alt={`Panashe Coach ${idx + 1}`} className="w-full h-72 object-cover object-center group-hover:scale-105 transition-transform duration-500" decoding="async" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="font-heading font-bold">Panashe Luxury Coach</p>
                    <p className="text-sm text-white/80">Air-conditioned • WiFi • USB Charging</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-panashe-red">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            Experience the Journey
          </h2>
          <p className="text-white/80 mb-8">Watch our coaches in action</p>
          <div className="relative rounded-lg overflow-hidden shadow-2xl">
            <video
              controls
              poster={PANASHE_IMAGES[0]}
              className="w-full"
            >
              <source src={PANASHE_VIDEO} type="video/mp4" />
              Your browser does not support video playback.
            </video>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-4">
            Ready to Travel?
          </h2>
          <p className="text-white/80 text-lg mb-8">
            Book your next journey with Panashe Coaches and experience premium cross-border travel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              className="bg-panashe-red hover:bg-panashe-red-dark text-white text-lg px-8 py-6"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              data-testid="cta-book-btn"
            >
              Book Now
            </Button>
            <Button 
              variant="outline" 
              className="border-2 border-white text-white hover:bg-white hover:text-panashe-red text-lg px-8 py-6"
              onClick={() => navigate('/track')}
              data-testid="cta-track-btn"
            >
              <MapPin className="mr-2 h-5 w-5" />
              Track Your Bus
            </Button>
            <Button
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-panashe-red text-lg px-8 py-6"
              onClick={() => navigate('/profile')}
              data-testid="cta-passenger-dashboard-btn"
            >
              <UserRound className="mr-2 h-5 w-5" />
              My Dashboard
            </Button>
          </div>
          
          {/* Contact Info */}
          <div className="mt-12 flex flex-wrap justify-center gap-8 text-white/70">
            <a href="tel:+263786563430" className="flex items-center gap-2 hover:text-panashe-gold transition-colors">
              <Phone className="h-5 w-5" />
              +263 78 656 3430
            </a>
            <a href="tel:+263777275154" className="flex items-center gap-2 hover:text-panashe-gold transition-colors">
              <Phone className="h-5 w-5" />
              +263 77 727 5154
            </a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default HomePage;
